import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';

import { editInvoiceApi, getInvoiceDetailsApi } from 'api/invoiceGroup';
import perfectRxLogo from 'assets/img/perfectRxLogo.png';
import LoadingModule from 'common/components/LoadingModule';

import {
  DEFAULT_CUSTOMER_MESSAGE,
  EMPTY_LINE,
  INVOICE_ACCENT_COLOR,
  currency,
  formatApiDate,
  isEmptyLine,
  isLineValid,
  lineAmount,
} from '../utils/invoiceEditHelpers';
import InvoiceLineItemsTable from './InvoiceLineItemsTable';

const InvoiceEdit = () => {
  const { invoiceGroupId, invoiceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Only reachable by clicking through from the invoice list, which sets this state flag —
  // this keeps the edit screen from being reachable via a typed/bookmarked/shared URL.
  const cameFromInvoiceList = Boolean(location.state?.fromInvoiceList);
  // Carried through from the invoice list so we can hand it back on save — the list needs the
  // group name and billTo to render, and there's no query to fetch them for a single group.
  const invoiceGroupName = location.state?.invoiceGroupName;
  const billTo = location.state?.billTo;

  const [invoice, setInvoice] = useState(null);
  const [lines, setLines] = useState([]);
  const [customerMessage, setCustomerMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [removeIdx, setRemoveIdx] = useState(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Line items need a stable React key that survives add/remove, independent of the
  // API's lineNumber (which newly-added rows don't have).
  const nextLineIdRef = useRef(0);

  useEffect(() => {
    document.title = `Invoice ${invoiceId} - SmartOS`;
  }, [invoiceId]);

  useEffect(() => {
    if (!cameFromInvoiceList) return undefined;

    let cancelled = false;

    setIsLoading(true);
    setIsError(false);

    getInvoiceDetailsApi({ invoiceId: Number(invoiceId) })
      .then((details) => {
        if (cancelled) return;
        const lineItems = details.invoiceLineItems ?? [];
        setInvoice(details);
        setCustomerMessage(details.customerMessage || DEFAULT_CUSTOMER_MESSAGE);
        setLines(
          lineItems.map((lineItem, idx) => ({
            id: idx,
            drugName: lineItem.drugName,
            ndc: lineItem.ndc,
            description: lineItem.description,
            quantity: String(lineItem.quantity),
            unitCost: String(lineItem.unitCost),
          }))
        );
        nextLineIdRef.current = lineItems.length;
      })
      .catch(() => {
        if (cancelled) return;
        setIsError(true);
        enqueueSnackbar('Failed to load invoice', { variant: 'error' });
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [invoiceId, cameFromInvoiceList]);

  const handleLineChange = (idx, field, value) =>
    setLines((prev) => prev.map((line, i) => (i === idx ? { ...line, [field]: value } : line)));

  const handleAddRow = () => {
    setLines((prev) => [...prev, { id: nextLineIdRef.current, ...EMPTY_LINE }]);
    nextLineIdRef.current += 1;
  };

  const handleConfirmRemoveRow = () => {
    setLines((prev) => prev.filter((_, i) => i !== removeIdx));
    setRemoveIdx(null);
  };

  const handleRemoveClick = (idx) => {
    if (isEmptyLine(lines[idx])) {
      setLines((prev) => prev.filter((_, i) => i !== idx));
      return;
    }
    setRemoveIdx(idx);
  };

  const handleConfirmSave = async () => {
    setIsSaving(true);
    try {
      const invoiceLineItems = lines.map((line, idx) => ({
        lineNumber: idx + 1,
        ndc: line.ndc,
        drugName: line.drugName,
        description: line.description,
        quantity: Number(line.quantity),
        unitCost: Number(line.unitCost),
      }));

      await editInvoiceApi({ invoiceId: Number(invoiceId), invoiceLineItems, customerMessage });
      setSaveDialogOpen(false);
      enqueueSnackbar('Invoice updated successfully', { variant: 'success' });
      // Hand back the group context the list needs, plus this invoice's id so the list reopens
      // its PDF — the user lands on the updated document rather than hunting for it.
      navigate(`/adminCenter/invoiceGroups/${invoiceGroupId}`, {
        state: { invoiceGroupName, billTo, openInvoiceId: Number(invoiceId) },
      });
    } catch (error) {
      enqueueSnackbar('Failed to update invoice', { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  if (!cameFromInvoiceList) {
    return <Navigate to={`/adminCenter/invoiceGroups/${invoiceGroupId}`} replace />;
  }

  if (isLoading) {
    return <LoadingModule height='50vh' />;
  }

  if (isError || !invoice) {
    return (
      <Grid container justifyContent='center' sx={{ py: 6 }}>
        <Typography color='error'>Unable to load invoice.</Typography>
      </Grid>
    );
  }

  const subtotal = lines.reduce((sum, line) => sum + lineAmount(line), 0);
  const tax = 0;
  const total = subtotal + tax;
  const isFormValid = lines.length > 0 && lines.every(isLineValid);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', px: 3 }}>
      <Grid
        container
        alignItems='center'
        justifyContent='space-between'
        sx={{ py: 2, flexShrink: 0 }}
      >
        <Grid item container alignItems='center' gap={1} sx={{ width: 'auto' }}>
          <IconButton
            component={Link}
            to={`/adminCenter/invoiceGroups/${invoiceGroupId}`}
            size='small'
          >
            <ArrowBackIcon fontSize='small' />
          </IconButton>
          <Typography component='h1' variant='h5'>
            Edit Invoice {invoiceId}
          </Typography>
        </Grid>
        <Grid item>
          <Button
            variant='contained'
            disabled={!isFormValid}
            onClick={() => setSaveDialogOpen(true)}
          >
            Save
          </Button>
        </Grid>
      </Grid>

      <Box sx={{ flex: 1, overflow: 'auto', pb: 3 }}>
        <Paper sx={{ p: { xs: 2, sm: 4 } }}>
          {/* Header: sender info + invoice meta */}
          <Grid container justifyContent='space-between' alignItems='flex-start' sx={{ mb: 4 }}>
            <Grid item>
              <img src={perfectRxLogo} alt='PerfectRx' height={32} />
              <Typography variant='body2' sx={{ mt: 1, fontWeight: 600 }}>
                PerfectRx LLC
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                ap@perfectrx.com
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                +1 (844) 597-6278
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                www.perfectrx.com
              </Typography>
            </Grid>
            <Grid item>
              <Typography
                variant='h4'
                sx={{
                  color: INVOICE_ACCENT_COLOR,
                  fontWeight: 700,
                  letterSpacing: 1,
                  mb: 1,
                  textAlign: 'right',
                }}
              >
                INVOICE
              </Typography>
              <Grid container spacing={1} sx={{ minWidth: 260 }}>
                <Grid item xs={6}>
                  <Typography variant='body2' color='text.secondary'>
                    Invoice No.
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>
                    {invoiceId}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant='body2' color='text.secondary'>
                    Terms
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>
                    Due on receipt
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant='body2' color='text.secondary'>
                    Invoice Date
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>
                    {formatApiDate(invoice.invoiceData.invoiceDate)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant='body2' color='text.secondary'>
                    Due Date
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>
                    {formatApiDate(invoice.invoiceData.dueDate)}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Bill To */}
          <Box sx={{ maxWidth: 320, mb: 4 }}>
            <Box sx={{ bgcolor: INVOICE_ACCENT_COLOR, color: 'common.white', px: 1.5, py: 0.5 }}>
              <Typography variant='caption' sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
                BILL TO
              </Typography>
            </Box>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderTop: 0, p: 1.5 }}>
              <Typography variant='body2'>
                {invoice.billTo.firstName} {invoice.billTo.lastName}
              </Typography>
              <Typography variant='body2'>{invoice.billTo.streetAddress}</Typography>
              {invoice.billTo.streetAddress2 && (
                <Typography variant='body2'>{invoice.billTo.streetAddress2}</Typography>
              )}
              <Typography variant='body2'>
                {invoice.billTo.city}, {invoice.billTo.state}, {invoice.billTo.zip}
              </Typography>
              <Typography variant='body2'>{invoice.billTo.email}</Typography>
              <Typography variant='body2'>{invoice.billTo.phone}</Typography>
            </Box>
          </Box>

          {/* Line items */}
          <InvoiceLineItemsTable
            lines={lines}
            onLineChange={handleLineChange}
            onRemoveClick={handleRemoveClick}
            onAddRow={handleAddRow}
          />

          {/* Totals */}
          <Grid container justifyContent='flex-end' sx={{ mb: 4 }}>
            <Grid item sx={{ minWidth: 260 }}>
              <Grid container justifyContent='space-between' sx={{ py: 0.5 }}>
                <Typography variant='body2' color='text.secondary'>
                  Subtotal
                </Typography>
                <Typography variant='body2' sx={{ fontWeight: 600 }}>
                  {currency(subtotal)}
                </Typography>
              </Grid>
              <Grid container justifyContent='space-between' sx={{ py: 0.5 }}>
                <Typography variant='body2' color='text.secondary'>
                  Tax
                </Typography>
                <Typography variant='body2' sx={{ fontWeight: 600 }}>
                  {currency(tax)}
                </Typography>
              </Grid>
              <Grid
                container
                justifyContent='space-between'
                sx={{
                  bgcolor: INVOICE_ACCENT_COLOR,
                  color: 'common.white',
                  px: 1.5,
                  py: 1,
                  mt: 1,
                }}
              >
                <Typography variant='body1' sx={{ fontWeight: 700 }}>
                  TOTAL
                </Typography>
                <Typography variant='body1' sx={{ fontWeight: 700 }}>
                  {currency(total)}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          {/* Note to customer */}
          <Box>
            <Typography variant='overline' sx={{ fontWeight: 700 }}>
              Note to customer
            </Typography>
            <TextField
              variant='standard'
              fullWidth
              multiline
              minRows={3}
              value={customerMessage}
              onChange={(e) => setCustomerMessage(e.target.value)}
              InputProps={{ sx: { fontStyle: 'italic', fontSize: 'body2.fontSize' } }}
            />
          </Box>
        </Paper>
      </Box>

      <Dialog open={removeIdx !== null} onClose={() => setRemoveIdx(null)} maxWidth='xs' fullWidth>
        <DialogTitle>Remove line item?</DialogTitle>
        <DialogContent>
          <Typography variant='body2'>
            This will remove the line item from the invoice and recalculate the totals.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant='contained' color='error' onClick={handleConfirmRemoveRow}>
            Remove
          </Button>
          <Button variant='outlined' onClick={() => setRemoveIdx(null)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Save changes to this invoice?</DialogTitle>
        <DialogContent>
          <Typography variant='body2'>This will update the invoice with your changes.</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant='contained' onClick={handleConfirmSave} disabled={isSaving}>
            Save
          </Button>
          <Button variant='outlined' onClick={() => setSaveDialogOpen(false)} disabled={isSaving}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceEdit;
